// Forked into ../../redesign/Results/GroupMetaproperties.tsx — keep in sync until phase_0 cleanup.
import React from 'react';
import { IconInbox } from '@bynder/icons';
import { MetapropertiesOption, SearchMetaproperty } from '../../types';
import {
	ClassicItem,
	ClassicNavigableHeadingButton,
	StyledHeadingNoButton,
	StyledHeadingWrapper,
	StyledImgWrapper,
	StyledInfoDiv,
	StyledItemsUL,
	StyledResultsSection,
} from '../StyledComponents';
import { navigateTo } from '../../../../helpers/windowLocation';
import { iconSizeExtraLarge } from '../../../../helpers/iconSizes';
import { getSpotlightResultClassName } from '../../helpers/getSpotlightResultClassName';
import { executeSearch, getMetapropertySearchField } from '../../helpers/searchUtils';
import { MetapropertySearchParams } from '../../helpers/types';
import { sortMetapropertyOptions } from '../../helpers/sortMetapropertyOptions';
import { useSearch, useSearchMode } from '../../SpotlightState';

interface Props {
	metaproperty: SearchMetaproperty;
}

export function GroupMetaproperties({ metaproperty }: Props) {
	const { clearResults } = useSearch();
	const { isHybridSearchOn } = useSearchMode();
	const shouldGoToPIMOnClick = metaproperty.spotlightFilterAction === 'pim';

	const handleOptionClick = (option: MetapropertiesOption) => {
		if (shouldGoToPIMOnClick) {
			navigateTo(`/show/${metaproperty.name}/${option.id}`);
		} else {
			const params: MetapropertySearchParams = {
				field: getMetapropertySearchField(metaproperty.name),
				value: option.name,
				filterType: 'add',
				filterKey: 'savedFilters',
			};

			const isPerformingNLSSearch = false;

			executeSearch(isHybridSearchOn, params, isPerformingNLSSearch, {
				metaproperty,
				option,
			});

			clearResults();
		}
	};

	const headingElement = shouldGoToPIMOnClick ? (
		<ClassicNavigableHeadingButton onSelect={() => navigateTo(`/show/${metaproperty.name}/`)}>
			<div>{metaproperty.label}</div>
		</ClassicNavigableHeadingButton>
	) : (
		<StyledHeadingWrapper>
			<StyledHeadingNoButton>
				<div>{metaproperty.label}</div>
			</StyledHeadingNoButton>
		</StyledHeadingWrapper>
	);

	return (
		<StyledResultsSection>
			{headingElement}
			<StyledItemsUL>
				{sortMetapropertyOptions(metaproperty).map((option) => (
					<ClassicItem
						dataId={option.id}
						onSelect={() => handleOptionClick(option)}
						className={getSpotlightResultClassName('MetapropertyOption')}
						key={option.id}
					>
						<StyledImgWrapper>
							{option.image ? (
								<img src={option.image} alt={option.label} />
							) : (
								<IconInbox size={iconSizeExtraLarge} />
							)}
						</StyledImgWrapper>
						<StyledInfoDiv>
							<div>{option.label}</div>
							{option.description && <div>{option.description}</div>}
						</StyledInfoDiv>
					</ClassicItem>
				))}
			</StyledItemsUL>
		</StyledResultsSection>
	);
}
